import React, { useEffect } from 'react';
import { ReportSubstantiation, UpdateReport } from 'src/api';
import { FormInput } from 'src/components/molecules';
import { HeaderSectionTitle } from 'src/components/organismsLegacy/CaseScreenHeader/CaseScreenHeader.styles';
import { getReadableReportSource } from 'src/components/pages/Case/helpers/getReadableReportSource';
import {
  CategoryDisplayType,
  MiReportSource,
  useGetDeterminedCategoriesQuery,
  useGetManualIntakeReportedCategoriesQuery,
} from 'src/graphql';
import {
  FormInputVariant,
  FormState,
  MiReportSourceForDisplay,
} from 'src/types';
import { DetailsContainer } from './DraftCaseDetails.styles';

interface Props {
  currentReport: UpdateReport | null;
  updateReport: (report: UpdateReport) => void;
  isExistingReport: boolean;
}

export const DraftCaseDetails = ({
  currentReport,
  isExistingReport,
  ...props
}: Props) => {
  const [source, setSource] = React.useState<MiReportSource>(
    '' as MiReportSource,
  );

  useEffect(() => {
    if (currentReport?.miReportSource) {
      setSource(currentReport.miReportSource);
    }
  }, [currentReport?.miReportSource]);

  const { data: determinedCategories } = useGetDeterminedCategoriesQuery();
  const { data: reportedCategories } =
    useGetManualIntakeReportedCategoriesQuery();

  useEffect(() => {
    // as we only get the category id when selecting a questionnaire, we need to get the category name from the reported categories list to update the report with both id and name of the category
    if (
      currentReport?.reportedCategory?.id &&
      !currentReport?.reportedCategory?.name &&
      reportedCategories
    ) {
      const reportedCategory =
        reportedCategories.getManualIntakeReportedCategories?.find(
          (category) => category.id === currentReport.reportedCategory?.id,
        );
      if (reportedCategory) {
        props.updateReport({
          ...currentReport,
          reportedCategory: {
            id: reportedCategory.id,
            name: reportedCategory.name,
          } as CategoryDisplayType,
        });
      }
    }
  }, [currentReport, reportedCategories, props]);

  const miReportSourceList = Object.values(MiReportSource) as MiReportSource[];

  const readableReportSource = getReadableReportSource(source);

  const miReportSourceLabel =
    readableReportSource &&
    readableReportSource !== MiReportSourceForDisplay.UNSPECIFIED
      ? readableReportSource
      : '';

  const onChangeSource = (value: string) => {
    props.updateReport({
      ...currentReport,
      miReportSource: value as MiReportSource,
    });
  };

  const onChangeDeterminedCategory = (value: { id: number; name: string }) => {
    props.updateReport({
      ...currentReport,
      determinedCategory: value as CategoryDisplayType,
    });
  };

  const onChangeSubstantiationStatus = (value: ReportSubstantiation) => {
    props.updateReport({
      ...currentReport,
      substantiation: value,
    });
  };

  return (
    <DetailsContainer>
      <HeaderSectionTitle>Case details</HeaderSectionTitle>
      <FormInput
        isRequired={false}
        label="Source"
        data-testid="source-form-input"
        variant={FormInputVariant.DROPDOWN}
        state={FormState.DEFAULT}
        options={miReportSourceList.map((value) => ({
          value,
          label: getReadableReportSource(value),
        }))}
        value={
          miReportSourceLabel
            ? {
                label: miReportSourceLabel,
                value: source,
              }
            : null
        }
        onChange={(option) => {
          if (option && !Array.isArray(option) && 'label' in option) {
            onChangeSource(option.value);
          }
        }}
        placeholder="Choose the source of the report"
      />
      {isExistingReport && (
        <>
          <FormInput
            label="Reported category"
            data-testid="reported-category-form-input"
            variant={FormInputVariant.DROPDOWN}
            state={FormState.DISABLED}
            options={[]}
            onChange={() => {}}
            placeholder="Choose reported category"
            value={
              currentReport?.reportedCategory?.name
                ? {
                    label: currentReport.reportedCategory.name,
                    value: currentReport.reportedCategory.name,
                  }
                : null
            }
          />
          <FormInput
            label="Determined category"
            data-testid="determined-category-form-input"
            variant={FormInputVariant.DROPDOWN}
            state={FormState.DEFAULT}
            options={(determinedCategories?.getDeterminedCategories || []).map(
              (value) => ({
                value: value.name,
                label: value.name,
                id: value.id,
              }),
            )}
            onChange={(option) => {
              if (
                option &&
                !Array.isArray(option) &&
                'label' in option &&
                'id' in option
              ) {
                onChangeDeterminedCategory({
                  id: option.id as number,
                  name: String(option.label),
                });
              }
            }}
            value={
              currentReport?.determinedCategory?.name
                ? {
                    label: currentReport.determinedCategory.name,
                    value: currentReport.determinedCategory.name,
                  }
                : null
            }
            placeholder="Choose determined category"
          />
          <FormInput
            label="Substantiation status"
            data-testid="substantiation-status-form-input"
            variant={FormInputVariant.DROPDOWN}
            state={FormState.DEFAULT}
            options={Object.values(ReportSubstantiation).map((status) => ({
              value: status,
              label: status,
            }))}
            onChange={(option) => {
              if (option && !Array.isArray(option) && 'label' in option) {
                onChangeSubstantiationStatus(
                  option.value as ReportSubstantiation,
                );
              }
            }}
            value={{
              label:
                currentReport?.substantiation ??
                ReportSubstantiation.UNDETERMINED,
              value:
                currentReport?.substantiation ??
                ReportSubstantiation.UNDETERMINED,
            }}
          />
        </>
      )}
    </DetailsContainer>
  );
};
